// src/pages/Settings.jsx
import React, { useState, useEffect } from "react";
import "../styles/Settings.css";
import { useAuth } from "../Context/AuthContext";
import { db, auth } from "../firebase/config";
import { getPortalUrl } from "../firebase/stripeFunctions";
import { verifyBeforeUpdateEmail, signOut } from "firebase/auth";

import {
  doc,
  updateDoc,
  getDoc,
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { parsePlace } from "../utils/parsePlace";
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  deleteUser,
  getIdToken,
} from "firebase/auth";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { timeZoneOptions } from "../constants/TimeZones";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiUser,
  FiBell,
  FiShield,
  FiCreditCard,
  FiEdit3,
  FiLock,
  FiMail,
  FiGlobe,
  FiTrash2,
  FiMapPin,
  FiAlertTriangle,

  // Notification settings redesign
  FiClock,
  FiBookOpen,
  FiFileText,
  FiCalendar,
  FiHeadphones,
  FiVolume2,
  FiDollarSign,
  FiCheckSquare,
  FiInfo,
  FiSave,
} from "react-icons/fi";
import { useSubscription } from "../hooks/useSubscription";
import { cancelSubscriptionAtPeriodEnd } from "../firebase/stripeFunctions";
import { increment } from "firebase/firestore";

import { DateTime } from "luxon";

/* ===================== PILOT NOTIFICATION ACCESS ===================== */
const pilotNotificationAccess = [
  "profileUpdate",
  "lateClockIn",
  "leaveRequest",
  "expenseSubmitted",
  "trainingCompleted",
  "trainingMissed",
  "ticketSubmitted",
  "platformUpdates",
  // ✅ Task Manager
  "taskSubmitted",
];
const NOTIFICATION_META = {
  lateClockIn: {
    description: "Get notified when employees clock in late.",
    icon: FiClock,
    tone: "red",
  },

  trainingCompleted: {
    description: "Get notified when training is completed.",
    icon: FiBookOpen,
    tone: "green",
  },

  leaveRequest: {
    description: "Get notified when a leave request is submitted.",
    icon: FiFileText,
    tone: "orange",
  },

  trainingMissed: {
    description: "Get notified when training deadlines are missed.",
    icon: FiCalendar,
    tone: "yellow",
  },

  profileUpdate: {
    description: "Get notified when employees update their profiles.",
    icon: FiUser,
    tone: "blue",
  },

  ticketSubmitted: {
    description: "Get notified when a new helpdesk ticket is submitted.",
    icon: FiHeadphones,
    tone: "purple",
  },

  platformUpdates: {
    description: "Receive important company announcements and updates.",
    icon: FiVolume2,
    tone: "teal",
  },

  expenseSubmitted: {
    description: "Get notified when an employee submits an expense.",
    icon: FiDollarSign,
    tone: "cyan",
  },

  taskSubmitted: {
    description: "Get notified when a task is submitted.",
    icon: FiCheckSquare,
    tone: "violet",
  },
};
/* ===================== PLAN ACCESS (no 'missedShift') ===================== */
const planNotificationAccess = {
  basic: ["profileUpdate", "lateClockIn"],
  standard: [
    "profileUpdate",
    "lateClockIn",
    "leaveRequest",
    "expenseSubmitted",
    // ✅ Task Manager
    "taskSubmitted",
  ],
  premium: [
    "profileUpdate",
    "lateClockIn",
    "leaveRequest",
    "trainingMissed",
    "trainingCompleted",
    "ticketSubmitted",
    "platformUpdates",
    "expenseSubmitted",

    // ✅ Task Manager
    "taskSubmitted",
  ],
  training: ["trainingMissed", "trainingCompleted"],
  helpdesk: ["ticketSubmitted"],
};

/* ============ RENDER ORDER (no 'missedShift' in the list) ============ */
const NOTIF_KEYS = [
  "lateClockIn",
  "trainingCompleted",
  "leaveRequest",
  "trainingMissed",
  "profileUpdate",
  "ticketSubmitted",
  "platformUpdates",
  "expenseSubmitted",
  // ✅ Task Manager
  "taskSubmitted",
];
// ===================== CANCELLATION / DELETE FEEDBACK =====================
const FEEDBACK_REASONS = [
  {
    id: "not_using",
    label: "We're not using Oiiko regularly.",
  },
  {
    id: "doesnt_fit",
    label: "Oiiko doesn't fully fit our HR/operations needs.",
  },
  {
    id: "too_expensive",
    label: "It's too expensive for our current budget.",
  },
  {
    id: "switched_tool",
    label: "We switched to another tool.",
  },
  {
    id: "onboarding_hard",
    label: "Setup / onboarding felt too difficult or time-consuming.",
  },
  {
    id: "team_resistance",
    label: "Our team prefers our previous system or process.",
  },
  {
    id: "short_term",
    label: "We only needed Oiiko for a short-term project.",
  },
  {
    id: "technical_issues",
    label: "We experienced technical issues or bugs.",
  },
  {
    id: "support",
    label: "We were not satisfied with support.",
  },
  {
    id: "downsizing",
    label: "Our company is downsizing or closing.",
  },
  {
    id: "privacy",
    label: "We have concerns about data privacy or security.",
  },
  {
    id: "other",
    label: "Other (please specify).",
  },
];

/** Helper: functions base URL */
const PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID;
const FUNCTIONS_ORIGIN =
  process.env.REACT_APP_FUNCTIONS_BASE ||
  (window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? `http://127.0.0.1:5001/${PROJECT_ID}/us-central1`
    : `https://us-central1-${PROJECT_ID}.cloudfunctions.net`);

function LegalPolicy() {
  const UPDATED = "November 3, 2025";
  return (
    <article
      className="legal-doc"
      style={{
        maxHeight: 600,
        overflow: "auto",
        lineHeight: 1.6,
        color: "black",
      }}
    >
      <h2>Oiiko Legal Policy</h2>
      <p>
        <em>Last updated: {UPDATED}</em>
      </p>
      <p>
        Welcome to Oiiko (“we,” “us,” or “our”). This document explains your
        rights and responsibilities when you use our website oiiko.com and our
        employee management platform (the “Service”). It combines our{" "}
        <strong>Privacy Policy</strong>, <strong>Terms of Use</strong>, and{" "}
        <strong>Data Processing Addendum (DPA)</strong> in one place for
        transparency and simplicity.
      </p>
      <p>If you use Oiiko, you agree to these terms.</p>

      <h3>1. About Oiiko</h3>
      <p>
        Oiiko LLC is a company registered in Texas, United States. Contact:{" "}
        <a href="mailto:support@oiiko.com">support@oiiko.com</a>.
      </p>
      <p>
        We provide an all-in-one employee management platform for businesses
        (“employers”) to manage their teams (“employees”).
      </p>

      <h3>2. Information We Collect</h3>
      <h4>A. Employer Account Data</h4>
      <ul>
        <li>Name, contact number, company email, company role</li>
        <li>Password and password strength indicator</li>
        <li>
          Company name, industry, team size, address (street, city,
          state/province, postal code, country)
        </li>
        <li>Latitude/longitude (auto-captured) and company time zone</li>
        <li>
          Stripe customer ID, selected plan, price ID, billing cycle,
          subscription status, trial dates, employee limit
        </li>
        <li>How you heard about us</li>
      </ul>

      <h4>B. Employee Data</h4>
      <ul>
        <li>
          Name, email, phone, gender, address, birth date, preferred first name
        </li>
        <li>
          Job title, department/group, role, employment type and status, start
          and end dates
        </li>
        <li>
          Onboarding flags (require onboarding, self-onboarding), profile
          completion status
        </li>
        <li>Time bank enabled, PTO hours, vacation hours, sick leave hours</li>
        <li>Documents and geocoded address details (lat/long)</li>
        <li>Company ID, employee document ID</li>
      </ul>

      <h4>C. Time Tracking and Scheduling</h4>
      <ul>
        <li>User ID, employee name, company ID, date</li>
        <li>
          Clock-in and clock-out time, hours worked, attendance status (on time,
          late, early)
        </li>
        <li>
          Shift start/end times, week range, assigned shift details (off day,
          holiday)
        </li>
        <li>Clock-in location (IP, optional coordinates)</li>
        <li>
          Company time zone and time-tracking settings (grace/early minutes)
        </li>
        <li>Notifications (lateClockIn with severity), daily/weekly totals</li>
        <li>
          Employer edits: was edited, edit reason, forced clock-out and audit
          fields
        </li>
      </ul>

      <h4>D. Scheduling, Leave, Expense, Training, Help Desk, Analytics</h4>
      <ul>
        <li>
          <strong>Scheduling</strong>: week start/end, repeat weekly, activity
          types, paid flag, hours and notifications
        </li>
        <li>
          <strong>Leave</strong>: type, dates (full/half-day), reason,
          files/URLs, status and decision logs, time zone, time bank deductions
        </li>
        <li>
          <strong>Expense</strong>: type, amount, merchant, method, date,
          description, receipt, status, reviewer notes, policy/budget settings
        </li>
        <li>
          <strong>Training</strong>: course meta (title, description, type, URL,
          deadline, thumbnail), assignments, progress, completion and
          notifications
        </li>
        <li>
          <strong>Help Desk</strong>: incident number, title, description,
          category, priority, impact, urgency, screenshots, SLA targets/breaches
        </li>
        <li>
          <strong>Analytics</strong>: aggregated metrics such as counts, rates,
          trends, top contributors across modules
        </li>
      </ul>

      <h3>3. How We Use Your Information</h3>
      <ul>
        <li>Operate and improve the platform and features</li>
        <li>Provide dashboards, reports, and analytics</li>
        <li>
          Send transactional emails (onboarding, notifications, password resets)
        </li>
        <li>Process payments and manage subscriptions via Stripe</li>
        <li>Support, troubleshooting, preventing abuse, and enforcing terms</li>
      </ul>
      <p>We do not sell personal data.</p>

      <h3>4. Data Storage and Security</h3>
      <p>
        We use Firebase, Firestore, Firebase Storage, and Stripe. Data is
        encrypted in transit and at rest within Google Cloud infrastructure.
        Access is restricted to authorized personnel for support or maintenance.
        If a data breach occurs we will notify affected users and regulators as
        required by law.
      </p>

      <h3>5. Data Sharing</h3>
      <p>We share limited data with trusted providers that help run Oiiko:</p>
      <ul>
        <li>Stripe for payments and subscriptions</li>
        <li>EmailJS for system emails</li>
      </ul>
      <p>
        Employers can access employee data they manage as part of the Service.
      </p>

      <h3>6. International Data Transfers</h3>
      <p>
        Data may be processed in the United States or other countries where our
        partners operate. For GDPR, we rely on Standard Contractual Clauses or
        equivalent safeguards.
      </p>

      <h3>7. Data Retention</h3>
      <p>
        We keep data while your account is active or as needed for legal and
        operational reasons. Employers can delete their accounts at any time.
        Limited records may be retained for audit, fraud prevention, or
        compliance.
      </p>

      <h3>8. Your Rights (GDPR &amp; CCPA)</h3>
      <p>
        You may request access, correction, deletion, portability, or
        restriction/objection where applicable. Email{" "}
        <a href="mailto:support@oiiko.com">support@oiiko.com</a>.
      </p>

      <h3>9. Children’s Privacy</h3>
      <p>Oiiko is not intended for individuals under 18.</p>

      <h3>10. Cookies and Tracking</h3>
      <p>We do not use cookies or third-party tracking at this time.</p>

      <h3>11. Subscriptions and Payments</h3>
      <ul>
        <li>Payments are processed by Stripe</li>
        <li>Free trials may be offered</li>
        <li>No refunds after payment</li>
        <li>Cancel subscription or delete account any time</li>
        <li>We may change prices/plans and will notify of material changes</li>
      </ul>

      <h3>12. User Responsibilities</h3>
      <ul>
        <li>Use Oiiko for lawful business purposes</li>
        <li>No copying, reverse engineering, scraping, or misuse</li>
        <li>Keep account credentials confidential</li>
        <li>Employers manage their employee accounts</li>
      </ul>

      <h3>13. Termination</h3>
      <p>
        Employers may terminate at any time. We may suspend or terminate for
        violations or fraud.
      </p>

      <h3>14. Intellectual Property</h3>
      <p>All content, features, and software are owned by Oiiko LLC.</p>

      <h3>15. Limitation of Liability</h3>
      <p>
        The Service is provided “as is.” We are not liable for indirect,
        incidental, or consequential damages. Our total liability is limited to
        amounts paid in the previous 12 months.
      </p>

      <h3>16. Indemnification</h3>
      <p>
        You agree to indemnify Oiiko for claims arising from your misuse or
        violations of these terms.
      </p>

      <h3>17. Governing Law</h3>
      <p>Texas, United States.</p>

      <h3>18. Changes</h3>
      <p>
        We may update this document. For major changes we will notify via email
        or in-app.
      </p>

      <hr />

      <h2>Data Processing Addendum (DPA)</h2>
      <p>
        This section applies when Oiiko processes personal data on behalf of
        employers (“Data Controllers”) as a “Data Processor.”
      </p>

      <h4>A. Scope and Purpose</h4>
      <p>
        We process employee data only to provide the Service (time, scheduling,
        leave, expenses, training, help desk) under the employer’s instructions.
      </p>

      <h4>B. Processor Obligations</h4>
      <ul>
        <li>Process data only on documented instructions</li>
        <li>Maintain appropriate technical and organizational security</li>
        <li>Ensure personnel confidentiality</li>
        <li>Notify the employer promptly of data breaches</li>
        <li>Assist with data subject requests</li>
        <li>Allow reasonable audits limited to verifying compliance</li>
      </ul>

      <h4>C. Sub-Processors</h4>
      <ul>
        <li>Google Firebase / Firestore / Storage (Google Cloud)</li>
        <li>Stripe, Inc.</li>
        <li>EmailJS</li>
      </ul>
      <p>
        Each sub-processor must provide equivalent data protection standards.
      </p>

      <h4>D. Deletion or Return</h4>
      <p>
        Upon termination or request we will delete or return personal data
        unless law requires retention.
      </p>

      <h4>E. International Transfers</h4>
      <p>
        When transferring outside the EEA we use appropriate safeguards such as
        Standard Contractual Clauses.
      </p>

      <hr />
      <h3>Contact</h3>
      <p>
        Oiiko LLC • <a href="mailto:support@oiiko.com">support@oiiko.com</a> •
        Registered in Texas, USA
      </p>
    </article>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [companyPilot, setCompanyPilot] = useState(null);

  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackContext, setFeedbackContext] = useState(null); // "cancel" | "delete"
  const [feedbackReasons, setFeedbackReasons] = useState([]);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [emailPassword, setEmailPassword] = useState("");

  // Email
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState(user?.email || "");
  useEffect(() => {
    if (user?.email && !editingEmail) {
      setNewEmail(user.email);
    }
  }, [user?.email, editingEmail]);

  const [savingEmail, setSavingEmail] = useState(false);

  // Password
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [activeTab, setActiveTab] = useState("account");
  // ✅ NEW: mobile "home" vs "detail" (drill-in)
  const [mobileView, setMobileView] = useState("home"); // "home" | "detail"
  const [mobileSearch, setMobileSearch] = useState("");

  // ✅ Optional: titles for mobile header when drilling in
  const TAB_TITLES = {
    account: "Account",
    notifications: "Notifications",
    locations: "Job Sites",
    privacy: "Privacy & Security",
    billing: "Billing",
  };

  // ✅ Mobile rows (matches iOS-like list)
  const MOBILE_ROWS = [
    { key: "account", label: "Account", icon: <FiUser /> },
    { key: "notifications", label: "Notifications", icon: <FiBell /> },
    // “Appearance” shown in screenshot (not implemented) → keep as disabled

    { key: "privacy", label: "Privacy & Security", icon: <FiShield /> },
    { key: "locations", label: "Job Sites", icon: <FiMapPin /> },
    { key: "billing", label: "Billing", icon: <FiCreditCard /> },
    // “Help and Support / About” (optional placeholders)
  ];
  // ✅ Billing portal button loading
  const [portalLoading, setPortalLoading] = useState(false);

  // Tabs for sidebar + mobile dropdown

  // Mobile detection (so dropdown only appears on small screens)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => {
      const mobile = !!mq.matches;
      setIsMobile(mobile);

      // ✅ When switching to mobile, always go to the home list
      if (mobile) {
        setMobileView("home");
        setMobileSearch("");
      } else {
        // ✅ When leaving mobile, ensure desktop shows whatever activeTab is
        setMobileView("detail");
      }
    };

    apply();
    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener(apply);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else mq.removeListener(apply);
    };
  }, []);

  // Time zone
  const [selectedTimeZone, setSelectedTimeZone] = useState(null);

  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [showInactiveLocations, setShowInactiveLocations] = useState(false);

  // ✅ Edit Location UI state
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  // keep same shape as newLocation for reuse
  const [editLocation, setEditLocation] = useState({
    name: "",
    street: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    country: "",
    geo: null,
  });

  const [newLocation, setNewLocation] = useState({
    name: "",
    street: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    country: "",
    geo: null,
  });

  // Company plan (fallback when subscription hook isn't ready)
  const [companyPlan, setCompanyPlan] = useState(null);
  const [companySub, setCompanySub] = useState(null);

  const [employeeCount, setEmployeeCount] = useState(0);
  const [employeeUsageLoading, setEmployeeUsageLoading] = useState(true);

  // ===== Delete Account UI state =====
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  // ✅ Generic confirm modal (replaces window.confirm)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmPrimaryLabel, setConfirmPrimaryLabel] = useState("Confirm");
  const [confirmDanger, setConfirmDanger] = useState(false);
  const confirmActionRef = React.useRef(null);

  const openConfirmModal = ({
    title,
    message,
    primaryLabel = "Confirm",
    danger = false,
    onConfirm,
  }) => {
    setConfirmTitle(title || "Please confirm");
    setConfirmMessage(message || "");
    setConfirmPrimaryLabel(primaryLabel);
    setConfirmDanger(!!danger);
    confirmActionRef.current = onConfirm || null;
    setConfirmOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmOpen(false);
    setConfirmTitle("");
    setConfirmMessage("");
    setConfirmPrimaryLabel("Confirm");
    setConfirmDanger(false);
    confirmActionRef.current = null;
  };

  const handleConfirmPrimary = async () => {
    const fn = confirmActionRef.current;
    closeConfirmModal();
    if (typeof fn === "function") {
      await fn();
    }
  };

  const { subscription, loading: subLoading } = useSubscription();
  const effectiveSubscription = subscription || companySub || null;

  const effectiveStatus = String(
    effectiveSubscription?.status || "",
  ).toLowerCase();

  const isFreePlan = effectiveStatus === "free";

  const employeeLimit =
    Number(effectiveSubscription?.employeeLimit ?? companySub?.employeeLimit) ||
    3;
  const [cancelScheduled, setCancelScheduled] = useState(false);
  const [cancelEffectiveSec, setCancelEffectiveSec] = useState(null);

  const isTrial = subscription?.status === "trialing";

  // --- Plan name (fallback to company plan like AdminDashboard) ---
  const displayPlan =
    effectiveSubscription?.planName ||
    (companyPlan
      ? companyPlan.charAt(0).toUpperCase() + companyPlan.slice(1)
      : "—");

  // --- ms→sec guard ---
  const toSec = (v) => {
    const n =
      typeof v === "object" && v?.seconds != null ? v.seconds : Number(v);
    if (!Number.isFinite(n)) return null;
    return n > 1e12 ? Math.floor(n / 1000) : n;
  };

  // --- Status + key timestamps (normalize to seconds) ---
  const subStatus = effectiveSubscription?.status || "—";
  const nowSec = Math.floor(Date.now() / 1000);

  const createdSec = toSec(subscription?.created);
  const trialEndSec = toSec(
    subscription?.trial_end ??
      subscription?.trialEnd ??
      (createdSec ? createdSec + 86400 : null),
  );
  const periodEndSec = toSec(subscription?.current_period_end);

  // --- Trial logic ---
  const isTrialing =
    subStatus === "trialing" && trialEndSec && nowSec < trialEndSec;

  const statusLabel = isFreePlan
    ? "Active"
    : isTrialing
    ? "Trialing"
    : subStatus === "active"
    ? "Active"
    : subStatus || "Unknown";

  // --- Final formatted strings ---
  const createdOn = createdSec
    ? DateTime.fromSeconds(createdSec).toFormat("MMMM dd, yyyy")
    : null;
  const trialEndsOn = trialEndSec
    ? DateTime.fromSeconds(trialEndSec).toFormat("MMMM dd, yyyy")
    : null;
  const currentPeriodEnd = periodEndSec
    ? DateTime.fromSeconds(periodEndSec).toFormat("MMMM dd, yyyy")
    : null;
  // If we know when it cancels (from company doc OR subscription object)
  // Stripe: cancel_at_period_end is BOOLEAN (true/false)
  // ✅ Stripe truth (if available)
  const stripeCancelAtPeriodEnd = !!subscription?.cancel_at_period_end;
  const stripeCancelAtSec = toSec(subscription?.cancel_at);

  // ✅ Company-doc fallback truth
  const companyCancelAtPeriodEnd = !!companySub?.cancelAtPeriodEnd;
  const companyCancelAtSec = toSec(companySub?.cancelAt);

  // some schemas store "subscriptionStatus": "cancelling"
  const companyIsCancelling =
    (companySub?.subscriptionStatus || companySub?.status || "")
      .toString()
      .toLowerCase() === "cancelling";

  // ✅ final truth used by UI
  const cancelAtPeriodEnd =
    stripeCancelAtPeriodEnd || companyCancelAtPeriodEnd || companyIsCancelling;

  const cancelAtSec = stripeCancelAtSec || companyCancelAtSec || null;

  const effectiveCancelSec =
    cancelEffectiveSec || cancelAtSec || periodEndSec || null;

  const cancelsOn =
    cancelScheduled && effectiveCancelSec
      ? DateTime.fromSeconds(effectiveCancelSec).toFormat("MMMM dd, yyyy")
      : null;

  // If cancellation is scheduled, show "Cancelling" as the status label
  const displayedStatusLabel = cancelScheduled ? "Cancelling" : statusLabel;

  // ✅ Keep cancelScheduled in sync with Stripe subscription truth
  useEffect(() => {
    // If we don't even know anything yet, clear local cancel UI
    if (!subscription && !companySub) {
      setCancelScheduled(false);
      setCancelEffectiveSec(null);
      return;
    }

    // If NOT cancelling (neither Stripe nor company doc says cancelling), clear it
    if (!cancelAtPeriodEnd) {
      setCancelScheduled(false);
      setCancelEffectiveSec(null);
      return;
    }

    // If cancelling, set it
    setCancelScheduled(true);

    // choose the best cancel date we have
    if (cancelAtSec) setCancelEffectiveSec(cancelAtSec);
    else if (periodEndSec) setCancelEffectiveSec(periodEndSec);
  }, [subscription, companySub, cancelAtPeriodEnd, cancelAtSec, periodEndSec]);

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const result = await cancelSubscriptionAtPeriodEnd();

      const when = result?.cancelAt
        ? new Date(result.cancelAt * 1000).toLocaleString()
        : null;

      // update local UI state so button/status update immediately
      if (result?.cancelAt) {
        setCancelScheduled(true);
        setCancelEffectiveSec(result.cancelAt);
      }

      if (isTrial) {
        toast.success(
          when
            ? `Your trial will end on ${when}. You will not be charged.`
            : "Your trial will end after the current period. You will not be charged.",
        );
      } else {
        toast.success(
          when
            ? `Subscription will be cancelled at the end of the current billing period (${when}).`
            : "Subscription will be cancelled at the end of the current billing period.",
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to cancel subscription");
    } finally {
      setCancelLoading(false);
      setShowCancelConfirm(false);
    }
  };

  // Optional: show included features by plan
  const FEATURES_BY_PLAN = {
    Free: [
      "Employee Management",
      "Time Tracking",
      "Shift Scheduling",
      "Payroll-ready Reports",
      "Multi-location Support",
    ],
    Basic: ["Employee", "Time Tracking", "Scheduling"],
    Standard: ["Employee", "Time Tracking", "Scheduling", "Leave", "Expense"],
    Premium: [
      "Employee",
      "Time",
      "Leave",
      "Scheduling",
      "Reports",
      "Training",
      "Help Desk",
      "Expense",
    ],
    Training: ["Employee", "Training", "Reports"],
    Helpdesk: ["Employee", "Help Desk"],
  };
  const included = FEATURES_BY_PLAN[displayPlan] || [];

  const handleBack = () => {
    navigate("/admin-dashboard"); // or whatever your dashboard route is
  };

  useEffect(() => {
    const fetchCompanyPlan = async () => {
      if (!user?.uid) return;

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const companyId = userSnap.data()?.companyId;
        if (!companyId) return;
        setCompanyId(companyId);
        if (!companyId) return;

        const companyRef = doc(db, "companies", companyId);
        const companySnap = await getDoc(companyRef);
        const companyData = companySnap.data() || {};
        try {
          setEmployeeUsageLoading(true);

          const employeesRef = collection(
            db,
            "companies",
            companyId,
            "employees",
          );

          const employeesQuery = query(
            employeesRef,
            where("status", "in", ["Pending", "Active"]),
          );

          const employeesSnap = await getDocs(employeesQuery);

          setEmployeeCount(employeesSnap.size);
        } catch (employeeError) {
          console.error("Failed to load employee usage:", employeeError);

          setEmployeeCount(0);
        } finally {
          setEmployeeUsageLoading(false);
        }
        setCompanyPilot(companyData.pilot || null);

        // ✅ store subscription object from company doc (fallback truth)
        setCompanySub(companyData.subscription || null);

        // ✅ plan fallback (prefer subscription.planName if you want)
        const planFromCompany = (
          companyData.subscription?.planName ||
          companyData.plan ||
          ""
        )
          .toString()
          .toLowerCase();

        setCompanyPlan(planFromCompany || null);

        setCompanyName(companyData.companyName || companyData.name || "");

        // pick up cancellation info if it exists
      } catch (err) {
        console.error("Failed to fetch company plan:", err);
      }
    };

    fetchCompanyPlan();
  }, [user?.uid]);

  useEffect(() => {
    const syncEmailIfNeeded = async () => {
      if (!auth.currentUser?.uid) return;

      // get latest auth email
      await auth.currentUser.reload();
      const authEmail = auth.currentUser.email;
      if (!authEmail) return;

      // only update if Firestore differs
      const userRef = doc(db, "users", auth.currentUser.uid);
      const snap = await getDoc(userRef);
      const fsEmail = (snap.data()?.email || "").toLowerCase();

      if (fsEmail !== authEmail.toLowerCase()) {
        await updateDoc(userRef, { email: authEmail });

        // keep customers mirror in sync too (if it exists)
        try {
          await updateDoc(doc(db, "customers", auth.currentUser.uid), {
            email: authEmail,
          });
        } catch {}
      }
    };

    syncEmailIfNeeded();
  }, []);

  useEffect(() => {
    if (!companyId) return;
    if (activeTab !== "locations") return;

    setLocationsLoading(true);

    (async () => {
      try {
        await refreshLocations();
      } finally {
        setLocationsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, activeTab]);

  // Fetch the saved company timezone from Firestore
  useEffect(() => {
    const fetchCompanyTimezone = async () => {
      if (!user?.uid) return;

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const companyId = userSnap.data()?.companyId;

        if (!companyId) throw new Error("Company ID not found");

        const companyRef = doc(db, "companies", companyId);
        const companySnap = await getDoc(companyRef);
        const savedTz = companySnap.data()?.timeZone;

        const matchingOption = timeZoneOptions.find(
          (opt) => opt.value === savedTz,
        ) || {
          value: savedTz,
          label: savedTz,
        };

        setSelectedTimeZone(matchingOption);
      } catch (err) {
        console.error("Failed to fetch time zone:", err);
      }
    };

    fetchCompanyTimezone();
  }, [user?.uid]);

  const isPilotActive = (pilot) => {
    if (!pilot?.enabled) return false;
    if (pilot.status && pilot.status !== "active") return false;

    if (pilot.endsAt?.seconds) {
      return pilot.endsAt.seconds > Math.floor(Date.now() / 1000);
    }
    return true;
  };
  const pilotActive = isPilotActive(companyPilot);
  const pilotEndsOn = companyPilot?.endsAt?.seconds
    ? DateTime.fromSeconds(companyPilot.endsAt.seconds).toFormat(
        "MMMM dd, yyyy",
      )
    : null;

  const effectiveNotificationAccess = pilotActive
    ? pilotNotificationAccess
    : planNotificationAccess[companyPlan] || [];

  /* ===================== NOTIFICATION STATE (no 'missedShift') ===================== */
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    user?.notificationsEnabled || false,
  );

  const [notificationPrefs, setNotificationPrefs] = useState(
    user?.notificationPrefs || {
      profileUpdate: true,
      lateClockIn: true,
      leaveRequest: true,
      trainingMissed: true,
      trainingCompleted: true,
      ticketSubmitted: true,
      platformUpdates: true,
      expenseSubmitted: true,
      // ✅ Task Manager
      taskSubmitted: true,
    },
  );

  // Normalize legacy keys & ensure no 'missedShift' key remains
  useEffect(() => {
    setNotificationPrefs((prev) => {
      const defaults = {
        profileUpdate: true,
        lateClockIn: true,
        leaveRequest: true,
        trainingMissed: true,
        trainingCompleted: true,
        ticketSubmitted: true,
        platformUpdates: true,
        expenseSubmitted: true,
        // ✅ Task Manager
        taskSubmitted: true,
      };

      // carry over previous values; map old keys if present
      const withRenames = {
        ...prev,
        platformUpdates:
          prev.platformUpdates ??
          prev.systemMaintenance ??
          defaults.platformUpdates,
        ticketSubmitted:
          prev.ticketSubmitted ?? prev.ticketUpdate ?? defaults.ticketSubmitted,
      };

      // drop old/removed keys
      const {
        systemMaintenance,
        ticketUpdate,
        missedShift, // ← ensure removed
        ...cleaned
      } = withRenames;

      return { ...defaults, ...cleaned };
    });
  }, []);

  const saveNotificationPrefs = async () => {
    try {
      const allowed = effectiveNotificationAccess;
      const pruned = Object.fromEntries(
        Object.entries(notificationPrefs).filter(([k]) => allowed.includes(k)),
      );

      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        notificationsEnabled,
        notificationPrefs: pruned,
      });

      // optional: mark quick-setup step and keep local state in sync
      const userSnap = await getDoc(userRef);
      const companyId = userSnap.data()?.companyId;
      if (companyId) {
        const companyRef = doc(db, "companies", companyId);
        await updateDoc(companyRef, {
          "quickSetupProgress.setNotifications": true,
        });
      }

      setNotificationPrefs(pruned);
      toast.success("Notification preferences saved.");
    } catch (err) {
      console.error("Error saving notification preferences:", err);
      toast.error("Failed to save preferences.");
    }
  };

  // 🔄 Email update
  const handleEmailUpdate = async () => {
    if (!auth.currentUser) return;

    const trimmed = (newEmail || "").trim().toLowerCase();
    if (!trimmed) return toast.error("Please enter a valid email.");
    if (!emailPassword)
      return toast.error("Please enter your current password to continue.");

    if (trimmed === (auth.currentUser.email || "").toLowerCase()) {
      return toast.info("That’s already your current email.");
    }

    setSavingEmail(true);

    try {
      // 1) Re-authenticate
      const cred = EmailAuthProvider.credential(
        auth.currentUser.email,
        emailPassword,
      );
      await reauthenticateWithCredential(auth.currentUser, cred);

      // 2) Send verification email for the NEW address (email does NOT change yet)
      await verifyBeforeUpdateEmail(auth.currentUser, trimmed, {
        url: `${window.location.origin}/verify-email`, // or a dedicated route if you want
        handleCodeInApp: false,
      });

      toast.success(
        "Verification email sent to your new address. Please verify it, then sign in again.",
      );

      // 3) Sign out immediately (your request)
      await signOut(auth);

      // Optional UI cleanup
      setEditingEmail(false);
      setEmailPassword("");
    } catch (err) {
      console.error("Email update failed:", err);
      toast.error(err?.message || "Failed to start email change.");
    } finally {
      setSavingEmail(false);
    }
  };

  // 🔐 Password update
  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword) {
      toast.error("Please fill in all fields.");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword,
      );

      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);

      setShowPasswordForm(false);
      setCurrentPassword("");
      setNewPassword("");
      toast.success("Password updated successfully!");
    } catch (err) {
      console.error("Password update error:", err);
      toast.error("Failed to update password: " + err.message);
    }
  };

  // ⏰ Time zone update
  const updateTimezone = async (tz) => {
    try {
      await updateDoc(doc(db, "users", user.uid), { timezone: tz });

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      const companyId = userSnap.data()?.companyId;

      if (!companyId) throw new Error("Company ID not found");

      const companyRef = doc(db, "companies", companyId);
      await updateDoc(companyRef, { timeZone: tz });

      toast.success("Timezone updated successfully.");
    } catch (err) {
      console.error("Timezone update error:", err);
      toast.error("Failed to update timezone.");
    }
  };

  const handleTimezoneChange = (newZone) => {
    if (newZone?.value === selectedTimeZone?.value) return;

    openConfirmModal({
      title: "Change time zone?",
      message: `Are you sure you want to change your time zone to "${newZone.label}"? This affects date/time display across your company.`,
      primaryLabel: "Change time zone",
      danger: false,
      onConfirm: async () => {
        setSelectedTimeZone(newZone);
        await updateTimezone(newZone.value);
      },
    });
  };

  const formatNotificationLabel = (key) => {
    const map = {
      profileUpdate: "Profile updates by employees",
      lateClockIn: "Late clock-in detected",
      leaveRequest: "Leave request submitted",
      trainingMissed: "Missed training deadline",
      trainingCompleted: "Training completed",
      ticketSubmitted: "New helpdesk ticket submitted",
      platformUpdates: "Announcements & updates",
      expenseSubmitted: "Employee submitted an expense",
      taskSubmitted: "Task submitted by employee",
    };
    return map[key] || key;
  };

  /* ================= Delete Account (self) =================
     Flow:
     1) Re-auth with password
     2) Call HTTPS function /deleteAccountSelf with ID token
     3) deleteUser(auth.currentUser)
     4) signOut and redirect to landing
  */
  const handleDeleteAccount = async () => {
    if (!auth.currentUser) return;
    if (!deleteConfirm) {
      toast.error("Please confirm you understand the action.");
      return;
    }
    if (!deletePassword) {
      toast.error("Please enter your password to continue.");
      return;
    }

    setDeleteLoading(true);
    try {
      // 1) Re-auth
      const cred = EmailAuthProvider.credential(
        auth.currentUser.email,
        deletePassword,
      );
      await reauthenticateWithCredential(auth.currentUser, cred);

      // 2) Get fresh ID token and call function
      const idToken = await getIdToken(auth.currentUser, true);
      const resp = await fetch(`${FUNCTIONS_ORIGIN}/deleteAccountSelf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({}),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.error || "Server error");
      }

      // 3) Delete auth user
      await deleteUser(auth.currentUser).catch(async (err) => {
        if (err?.code !== "auth/requires-recent-login") throw err;
        await signOut(auth);
      });

      // 4) Sign out + go home
      await signOut(auth).catch(() => {});
      toast.success("Your account has been deleted.");
      setTimeout(() => (window.location.href = "/"), 600);
    } catch (e) {
      console.error("delete account error:", e);
      toast.error(e.message || "Failed to delete account.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (!auth.currentUser) return;

    if (!deleteConfirm) {
      toast.error("Please confirm you understand this action is permanent.");
      return;
    }
    if (!deletePassword) {
      toast.error("Please enter your password to continue.");
      return;
    }

    // Open feedback modal first
    setFeedbackContext("delete");
    setShowFeedbackModal(true);
  };

  const toggleFeedbackReason = (id) => {
    setFeedbackReasons((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  const resetFeedbackState = () => {
    setShowFeedbackModal(false);
    setFeedbackContext(null);
    setFeedbackReasons([]);
    setFeedbackText("");
    setFeedbackSaving(false);
  };

  const handleFeedbackContinue = async (submitFeedback) => {
    const ctx = feedbackContext;
    if (!ctx) {
      resetFeedbackState();
      return;
    }

    const hasFeedback =
      feedbackReasons.length > 0 || feedbackText.trim().length > 0;

    try {
      if (submitFeedback && hasFeedback) {
        setFeedbackSaving(true);

        const basePayload = {
          context: ctx, // "cancel" or "delete"
          reasons: feedbackReasons,
          feedbackText: feedbackText.trim(),
          planName: subscription?.planName || null,
          subscriptionStatus: subscription?.status || null,
          userId: user?.uid || null,
          createdAt: serverTimestamp(),
        };
        // ⬇️ Enrich with company info for the global collection
        const payloadWithCompany = {
          ...basePayload,
          companyId: companyId || null,
          companyName: companyName || null,
        };

        // 1) Company-scoped feedback (if we know the company)
        if (companyId) {
          const companyFeedbackRef = collection(
            db,
            "companies",
            companyId,
            "feedback",
          );
          await addDoc(companyFeedbackRef, payloadWithCompany);
        }

        // 2) Global feedback collection (always)
        const globalFeedbackRef = collection(db, "globalFeedback");
        await addDoc(globalFeedbackRef, payloadWithCompany);
      }
    } catch (err) {
      console.error("Failed to save feedback:", err);
      // we don't block cancellation/delete if feedback fails
    } finally {
      setFeedbackSaving(false);
    }

    // Close modal + clear fields
    resetFeedbackState();

    // Continue with the action they started
    if (ctx === "cancel") {
      setShowCancelConfirm(true); // show your existing cancel confirm modal
    } else if (ctx === "delete") {
      // run your existing delete logic
      await handleDeleteAccount();
    }
  };

  const cancelButtonDisabled = cancelLoading || cancelScheduled || pilotActive;

  const refreshLocations = async () => {
    if (!companyId) return;
    try {
      const ref = collection(db, "companies", companyId, "locations");
      const snap = await getDocs(ref);
      setLocations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
    }
  };

  const setLocationActive = async (locationId, nextActive, location) => {
    if (!companyId) return;

    // ✅ primary locked
    if (location?.isPrimary && nextActive === false) {
      toast.error("Primary location cannot be deactivated.");
      return;
    }

    const label = nextActive ? "reactivate" : "deactivate";

    openConfirmModal({
      title: nextActive ? "Reactivate location?" : "Deactivate location?",
      message: `Are you sure you want to ${label} "${location?.name ||
        "this location"}"?`,
      primaryLabel: nextActive ? "Reactivate" : "Deactivate",
      danger: !nextActive,
      onConfirm: async () => {
        try {
          const locRef = doc(
            db,
            "companies",
            companyId,
            "locations",
            locationId,
          );

          await updateDoc(locRef, {
            active: nextActive,
            ...(nextActive
              ? {
                  reactivatedAt: serverTimestamp(),
                  reactivatedBy: user.uid,
                }
              : {
                  deactivatedAt: serverTimestamp(),
                  deactivatedBy: user.uid,
                }),
          });

          toast.success(
            nextActive ? "Location reactivated." : "Location deactivated.",
          );

          await refreshLocations();
        } catch (e) {
          console.error(e);
          toast.error("Failed to update location.");
        }
      },
    });
  };

  // ✅ Open edit modal (prefill form from selected location)
  const openEditLocation = (loc) => {
    if (!loc) return;

    const a = loc.address || {};
    setEditingLocation(loc);

    setEditLocation({
      name: loc.name || "",
      street: a.street || "",
      city: a.city || "",
      stateProvince: a.stateProvince || "",
      postalCode: a.postalCode || "",
      country: a.country || "",
      geo: a.geo || null,
    });

    setEditOpen(true);
  };

  const closeEditLocation = () => {
    setEditOpen(false);
    setEditSaving(false);
    setEditingLocation(null);
    setEditLocation({
      name: "",
      street: "",
      city: "",
      stateProvince: "",
      postalCode: "",
      country: "",
      geo: null,
    });
  };

  // ✅ Save edited location to Firestore
  const saveEditedLocation = async () => {
    if (!companyId || !editingLocation?.id) return;
    if (editingLocation?.isPrimary) {
      toast.error("Primary location can only be edited in Employer Profile.");
      return;
    }
    // Basic validation
    if (!editLocation.name.trim() || !editLocation.street.trim()) {
      toast.error("Please enter a location name and address.");
      return;
    }

    // Optional: if you want to lock editing for primary, uncomment:
    // if (editingLocation.isPrimary) {
    //   toast.error("Primary location cannot be edited here.");
    //   return;
    // }

    setEditSaving(true);

    try {
      const locRef = doc(
        db,
        "companies",
        companyId,
        "locations",
        editingLocation.id,
      );

      await updateDoc(locRef, {
        name: editLocation.name.trim(),
        address: {
          street: editLocation.street,
          city: editLocation.city,
          stateProvince: editLocation.stateProvince,
          postalCode: editLocation.postalCode,
          country: editLocation.country,
          geo: editLocation.geo || null,
        },
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
      });

      toast.success("Location updated.");

      // refresh list
      await refreshLocations();

      closeEditLocation();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update location.");
    } finally {
      setEditSaving(false);
    }
  };

  // OPTIONAL (only if you want a delete button now)
  // Safer: keep delete simple for now (non-primary only). Later we’ll add reference checks.
  const deleteLocation = async (locationId, location) => {
    if (!companyId) return;

    if (location?.isPrimary) {
      toast.error("Primary location cannot be deleted.");
      return;
    }

    openConfirmModal({
      title: "Delete location permanently?",
      message: `Delete "${location?.name ||
        "this location"}" permanently? This is not recommended if it may be referenced by schedules/time entries later.`,
      primaryLabel: "Delete",
      danger: true,
      onConfirm: async () => {
        try {
          const locRef = doc(
            db,
            "companies",
            companyId,
            "locations",
            locationId,
          );
          await deleteDoc(locRef);
          toast.success("Location deleted.");
          await refreshLocations();
        } catch (e) {
          console.error(e);
          toast.error("Failed to delete location.");
        }
      },
    });
  };
  const employeeUsagePercent =
    employeeLimit > 0
      ? Math.min(100, Math.round((employeeCount / employeeLimit) * 100))
      : 0;
  return (
    <div className="websettings-page">
      <div className="websettings-wrapper">
        <ToastContainer position="top-right" />

        <div className="settings-sidebar">
          <div className="settings-sidebar__title">SETTINGS</div>

          <button
            className={`sidebar-link ${
              activeTab === "account" ? "active" : ""
            }`}
            onClick={() => setActiveTab("account")}
          >
            <FiUser className="sidebar-link__icon" />
            <span>Account Settings</span>
          </button>

          <button
            className={`sidebar-link ${
              activeTab === "notifications" ? "active" : ""
            }`}
            onClick={() => setActiveTab("notifications")}
          >
            <FiBell className="sidebar-link__icon" />
            <span>Notifications</span>
          </button>
          <button
            className={`sidebar-link ${
              activeTab === "locations" ? "active" : ""
            }`}
            onClick={() => setActiveTab("locations")}
          >
            <FiMapPin className="sidebar-link__icon" />
            <span>Job Sites</span>
          </button>

          <button
            className={`sidebar-link ${
              activeTab === "privacy" ? "active" : ""
            }`}
            onClick={() => setActiveTab("privacy")}
          >
            <FiShield className="sidebar-link__icon" />
            <span>Privacy Policy</span>
          </button>

          <button
            className={`sidebar-link ${
              activeTab === "billing" ? "active" : ""
            }`}
            onClick={() => setActiveTab("billing")}
          >
            <FiCreditCard className="sidebar-link__icon" />
            <span>Billing</span>
          </button>
        </div>

        <div className="settings-content">
          <header className="settings-header">
            {isMobile ? (
              <div className="settings-mobile-topbar">
                {mobileView === "home" ? (
                  <>
                    <button
                      className="settings-mobile-back"
                      onClick={handleBack}
                    >
                      <FiChevronLeft />
                    </button>
                    <div className="settings-mobile-title">Settings</div>
                    <div className="settings-mobile-spacer" />
                  </>
                ) : (
                  <>
                    <button
                      className="settings-mobile-back"
                      onClick={() => {
                        setMobileView("home");
                        setMobileSearch("");
                      }}
                    >
                      <FiChevronLeft />
                    </button>
                    <div className="settings-mobile-title">
                      {TAB_TITLES[activeTab] || "Settings"}
                    </div>
                    <div className="settings-mobile-spacer" />
                  </>
                )}
              </div>
            ) : (
              <div>
                <button className="btn btn-ghost" onClick={handleBack}>
                  <FiChevronLeft /> Back to Dashboard
                </button>
              </div>
            )}
          </header>
          {/* ✅ MOBILE SETTINGS HOME (iOS-style) */}
          {isMobile && mobileView === "home" && (
            <div className="settings-mobile-home">
              <div className="settings-mobile-search">
                <input
                  className="settings-mobile-search-input"
                  value={mobileSearch}
                  onChange={(e) => setMobileSearch(e.target.value)}
                  placeholder="Search for a setting..."
                />
              </div>

              <div className="settings-mobile-list">
                {MOBILE_ROWS.filter((row) =>
                  row.label
                    .toLowerCase()
                    .includes((mobileSearch || "").toLowerCase()),
                ).map((row) => (
                  <button
                    key={row.key}
                    className={`settings-mobile-row ${
                      row.disabled ? "is-disabled" : ""
                    }`}
                    onClick={() => {
                      if (row.disabled) {
                        toast.info("Coming soon.");
                        return;
                      }
                      setActiveTab(row.key);
                      setMobileView("detail");
                    }}
                  >
                    <div className="settings-mobile-row-left">
                      <div className="settings-mobile-ico">{row.icon}</div>
                      <div className="settings-mobile-label">{row.label}</div>
                    </div>
                    <div className="settings-mobile-chevron">›</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ======================= ACCOUNT TAB ======================= */}
          {(!isMobile || mobileView === "detail") && activeTab === "account" && (
            <div className="account-settings-redesign">
              {/* =====================================================
        PASSWORD
    ====================================================== */}
              <section className="account-setting-card account-setting-password">
                <div className="account-setting-main">
                  <div className="account-setting-icon">
                    <FiLock />
                  </div>

                  <div className="account-setting-copy">
                    <h3>Change Password</h3>
                    <p>Keep your account secure by using a strong password.</p>
                  </div>
                </div>

                {!showPasswordForm && (
                  <div className="account-setting-action-wrap">
                    <button
                      type="button"
                      className="account-setting-action-btn account-password-btn"
                      onClick={() => setShowPasswordForm(true)}
                    >
                      <FiEdit3 />
                      <span>Change Password</span>
                    </button>
                  </div>
                )}

                {showPasswordForm && (
                  <div className="account-setting-expanded">
                    <div className="account-form-grid">
                      <div className="account-form-field">
                        <label>Current password</label>

                        <input
                          type="password"
                          placeholder="Enter current password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="input account-input"
                        />
                      </div>

                      <div className="account-form-field">
                        <label>New password</label>

                        <input
                          type="password"
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="input account-input"
                        />
                      </div>
                    </div>

                    <div className="account-form-actions">
                      <button
                        type="button"
                        className="account-primary-btn"
                        onClick={handlePasswordChange}
                      >
                        Save Password
                      </button>

                      <button
                        type="button"
                        className="account-secondary-btn"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setCurrentPassword("");
                          setNewPassword("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </section>

              {/* =====================================================
        EMAIL
    ====================================================== */}
              <section className="account-setting-card account-setting-email">
                <div className="account-setting-main">
                  <div className="account-setting-icon">
                    <FiMail />
                  </div>

                  <div className="account-setting-copy">
                    <h3>Change Email</h3>
                    <p>
                      Update the email address associated with your account.
                    </p>
                  </div>
                </div>

                {!editingEmail && (
                  <>
                    <div className="account-current-email">
                      {user?.email || "No email available"}
                    </div>

                    <div className="account-setting-action-wrap">
                      <button
                        type="button"
                        className="account-setting-action-btn account-email-btn"
                        onClick={() => setEditingEmail(true)}
                      >
                        <FiEdit3 />
                        <span>Edit Email</span>
                      </button>
                    </div>
                  </>
                )}

                {editingEmail && (
                  <div className="account-setting-expanded">
                    <div className="account-form-grid">
                      <div className="account-form-field">
                        <label>New email address</label>

                        <input
                          type="email"
                          placeholder="New email address"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className="input account-input"
                        />
                      </div>

                      <div className="account-form-field">
                        <label>Current password</label>

                        <input
                          type="password"
                          placeholder="Current password"
                          value={emailPassword}
                          onChange={(e) => setEmailPassword(e.target.value)}
                          className="input account-input"
                        />
                      </div>
                    </div>

                    <div className="account-form-actions">
                      <button
                        type="button"
                        className="account-primary-btn"
                        onClick={handleEmailUpdate}
                        disabled={
                          savingEmail || !newEmail?.trim() || !emailPassword
                        }
                      >
                        {savingEmail ? "Saving…" : "Save Email"}
                      </button>

                      <button
                        type="button"
                        className="account-secondary-btn"
                        onClick={() => {
                          setEditingEmail(false);
                          setEmailPassword("");
                          setNewEmail(user?.email || "");
                        }}
                        disabled={savingEmail}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </section>

              {/* =====================================================
        TIME ZONE
    ====================================================== */}
              <section className="account-setting-card account-setting-timezone">
                <div className="account-timezone-top">
                  <div className="account-setting-main">
                    <div className="account-setting-icon">
                      <FiGlobe />
                    </div>

                    <div className="account-setting-copy">
                      <h3>Time Zone</h3>

                      <p>
                        This affects date and time display across your company.
                      </p>
                    </div>
                  </div>

                  {selectedTimeZone && (
                    <div className="account-current-timezone">
                      <div className="account-current-timezone-icon">
                        <FiClock />
                      </div>

                      <div>
                        <span>Current Time Zone</span>

                        <strong>
                          {selectedTimeZone.label || selectedTimeZone.value}
                        </strong>
                      </div>
                    </div>
                  )}
                </div>

                {selectedTimeZone && (
                  <div className="account-timezone-select-area">
                    <label>Select Time Zone</label>

                    <Select
                      options={timeZoneOptions}
                      value={selectedTimeZone}
                      onChange={(opt) => handleTimezoneChange(opt)}
                      className="account-timezone-select"
                      classNamePrefix="account-timezone-react"
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "100%",
                        }),
                      }}
                    />
                  </div>
                )}
              </section>

              {/* =====================================================
        DANGER ZONE
    ====================================================== */}
              <section className="account-danger-card">
                <div className="account-danger-main">
                  <div className="account-danger-icon">
                    <FiShield />
                  </div>

                  <div>
                    <h3>Danger Zone</h3>

                    <p>Permanently delete your account and all related data.</p>
                  </div>
                </div>

                {!deleteOpen ? (
                  <>
                    <div className="account-danger-warning">
                      <FiAlertTriangle />

                      <div>
                        <strong>
                          This action is permanent and cannot be undone.
                        </strong>

                        <span>
                          This permanently deletes your account, profile data,
                          time-entries, schedules and files.
                        </span>
                      </div>
                    </div>

                    <div className="account-danger-action">
                      <button
                        type="button"
                        className="account-delete-btn"
                        onClick={() => setDeleteOpen(true)}
                      >
                        <FiTrash2 />
                        <span>Delete Account</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="account-delete-expanded">
                    <div className="account-danger-warning">
                      <FiAlertTriangle />

                      <div>
                        <strong>Confirm permanent account deletion.</strong>

                        <span>
                          Enter your password and confirm that you understand
                          this action cannot be reversed.
                        </span>
                      </div>
                    </div>

                    <div className="account-delete-form">
                      <div className="account-form-field">
                        <label>Confirm password to continue</label>

                        <input
                          type="password"
                          className="input account-input"
                          placeholder="Your password"
                          value={deletePassword}
                          onChange={(e) => setDeletePassword(e.target.value)}
                        />
                      </div>

                      <label className="account-delete-confirm">
                        <input
                          type="checkbox"
                          className="tiny-checkbox"
                          checked={deleteConfirm}
                          onChange={(e) => setDeleteConfirm(e.target.checked)}
                        />

                        <span>
                          I understand this action is permanent and cannot be
                          undone.
                        </span>
                      </label>

                      <div className="account-form-actions">
                        <button
                          type="button"
                          className="account-delete-btn"
                          onClick={handleDeleteClick}
                          disabled={deleteLoading}
                          title="This is permanent"
                        >
                          <FiTrash2 />

                          <span>
                            {deleteLoading ? "Deleting…" : "Delete Account"}
                          </span>
                        </button>

                        <button
                          type="button"
                          className="account-secondary-btn"
                          onClick={() => {
                            setDeleteOpen(false);
                            setDeletePassword("");
                            setDeleteConfirm(false);
                          }}
                          disabled={deleteLoading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            </div>
          )}

          {/* ======================= NOTIFICATIONS TAB ======================= */}
          {(!isMobile || mobileView === "detail") &&
            activeTab === "notifications" && (
              <section className="notification-settings-card">
                {/* ================= HEADER ================= */}
                <div className="notification-settings-header">
                  <div className="notification-settings-heading">
                    <div className="notification-settings-main-icon">
                      <FiBell />
                    </div>

                    <div>
                      <h3>Notification Settings</h3>

                      <p>Choose which alerts you want to receive and how.</p>
                    </div>
                  </div>

                  {/* ENABLE ALL */}
                  <div className="notification-enable-all">
                    <div className="notification-enable-copy">
                      <strong>Enable All Notifications</strong>
                      <span>Turn on or off all notifications</span>
                    </div>

                    <label className="notification-switch">
                      <input
                        type="checkbox"
                        checked={notificationsEnabled}
                        onChange={() =>
                          setNotificationsEnabled(!notificationsEnabled)
                        }
                      />

                      <span className="notification-switch-slider" />
                    </label>
                  </div>
                </div>

                {/* ================= INFO BANNER ================= */}
                <div className="notification-info-banner">
                  <FiInfo />

                  <span>
                    You'll receive real-time alerts for important updates and
                    activities.
                  </span>
                </div>

                {/* ================= NOTIFICATION GRID ================= */}
                <div
                  className={`notification-preferences-grid ${
                    !notificationsEnabled
                      ? "notification-preferences-disabled"
                      : ""
                  }`}
                >
                  {NOTIF_KEYS.filter((key) =>
                    effectiveNotificationAccess.includes(key),
                  ).map((key) => {
                    const meta = NOTIFICATION_META[key] || {};
                    const NotificationIcon = meta.icon || FiBell;

                    return (
                      <div
                        key={key}
                        className={`notification-preference-card notification-tone-${meta.tone ||
                          "blue"}`}
                      >
                        {/* Icon */}
                        <div className="notification-preference-icon">
                          <NotificationIcon />
                        </div>

                        {/* Text */}
                        <div className="notification-preference-content">
                          <div className="notification-preference-title">
                            {formatNotificationLabel(key)}
                          </div>

                          <div className="notification-preference-description">
                            {meta.description ||
                              "Receive notifications for this activity."}
                          </div>
                        </div>

                        {/* Individual toggle */}
                        <label className="notification-switch notification-switch-small">
                          <input
                            type="checkbox"
                            checked={!!notificationPrefs[key]}
                            onChange={() =>
                              setNotificationPrefs((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }))
                            }
                            disabled={!notificationsEnabled}
                          />

                          <span className="notification-switch-slider" />
                        </label>
                      </div>
                    );
                  })}
                </div>

                {/* ================= FOOTER ================= */}
                <div className="notification-settings-footer">
                  <div className="notification-footer-note">
                    <div className="notification-footer-note-icon">
                      <FiShield />
                    </div>

                    <div>
                      <strong>Stay informed, always</strong>

                      <span>You can change these preferences at any time.</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="notification-save-btn"
                    onClick={saveNotificationPrefs}
                  >
                    <FiSave />

                    <span>Save Preferences</span>
                  </button>
                </div>
              </section>
            )}
          {(!isMobile || mobileView === "detail") && activeTab === "locations" && (
            <section className="card">
              <div className="card-head">
                <div className="card-head__icon">
                  <FiMapPin />
                </div>
                <div>
                  <h3 className="card-title">Locations</h3>
                  <p className="card-desc">
                    Manage your business locations. Your primary location is set
                    during setup.
                  </p>
                </div>
              </div>

              <div className="locations-layout">
                {/* LEFT: Locations list */}
                <div className="locations-panel">
                  <div className="locations-panel__top">
                    <div>
                      <div className="locations-panel__title">
                        Your locations
                      </div>
                      <div className="locations-panel__hint">
                        {locationsLoading
                          ? "Loading…"
                          : `${locations.length} total`}
                      </div>
                    </div>

                    <div className="toggle-wrap" style={{ marginLeft: "auto" }}>
                      <span className="toggle-label">Show inactive</span>
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={showInactiveLocations}
                          onChange={() => setShowInactiveLocations((v) => !v)}
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>

                  {locationsLoading ? (
                    <div className="hint">Loading locations…</div>
                  ) : (
                    (() => {
                      const filtered = locations.filter((l) =>
                        showInactiveLocations ? true : l.active !== false,
                      );

                      if (filtered.length === 0) {
                        return (
                          <div className="locations-empty">
                            <div className="locations-empty__title">
                              {showInactiveLocations
                                ? "No locations found"
                                : "No active locations"}
                            </div>
                            <div className="locations-empty__text">
                              {showInactiveLocations
                                ? "Try adding a new location."
                                : "Turn on “Show inactive” to view deactivated locations."}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="locations-list">
                          {filtered.map((l) => {
                            const addressLine = [
                              l.address?.street,
                              l.address?.city,
                              l.address?.stateProvince,
                              l.address?.postalCode,
                              l.address?.country,
                            ]
                              .filter(Boolean)
                              .join(", ");

                            return (
                              <div
                                key={l.id}
                                className={`location-card ${
                                  l.active === false ? "inactive" : ""
                                }`}
                              >
                                <div className="location-card__row">
                                  <div className="location-card__name">
                                    {l.name || "Unnamed location"}
                                  </div>

                                  {l.isPrimary ? (
                                    <span className="location-badge location-badge--primary">
                                      Primary
                                    </span>
                                  ) : (
                                    <span className="location-badge location-badge--secondary">
                                      Site
                                    </span>
                                  )}
                                </div>

                                <div className="location-card__meta">
                                  <FiMapPin
                                    style={{ marginRight: 6, opacity: 0.7 }}
                                  />
                                  <span>
                                    {addressLine || "No address saved"}
                                  </span>
                                </div>

                                <div className="location-card__footer">
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 10,
                                    }}
                                  >
                                    <span
                                      className={`location-dot ${
                                        l.active ? "on" : "off"
                                      }`}
                                    />
                                    <span className="location-status">
                                      {l.active ? "Active" : "Inactive"}
                                    </span>
                                  </div>

                                  <div style={{ display: "flex", gap: 8 }}>
                                    {!l.isPrimary && (
                                      <button
                                        className="btn btn-ghost"
                                        onClick={() => openEditLocation(l)}
                                        title="Edit location"
                                      >
                                        Edit
                                      </button>
                                    )}

                                    {!l.isPrimary &&
                                      (l.active ? (
                                        <button
                                          className="btn btn-ghost"
                                          title="Deactivate location"
                                          onClick={() =>
                                            setLocationActive(l.id, false, l)
                                          }
                                        >
                                          Deactivate
                                        </button>
                                      ) : (
                                        <button
                                          className="btn btn-ghost"
                                          onClick={() =>
                                            setLocationActive(l.id, true, l)
                                          }
                                        >
                                          Reactivate
                                        </button>
                                      ))}

                                    {!l.isPrimary && (
                                      <button
                                        className="btn btn-ghost"
                                        onClick={() => deleteLocation(l.id, l)}
                                      >
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  )}
                </div>

                {/* RIGHT: Add location */}
                <div className="locations-panel">
                  <div className="locations-panel__top">
                    <div>
                      <div className="locations-panel__title">
                        Add a location
                      </div>
                      <div className="locations-panel__hint">
                        Give it a name, then pick an address.
                      </div>
                    </div>
                  </div>

                  <div className="locations-form">
                    <label className="label">Location name</label>
                    <input
                      className="input"
                      placeholder="e.g., Main Office, Warehouse, Site B"
                      value={newLocation.name}
                      onChange={(e) =>
                        setNewLocation((s) => ({ ...s, name: e.target.value }))
                      }
                    />

                    <label className="label" style={{ marginTop: 10 }}>
                      Address
                    </label>
                    <AddressAutocomplete
                      value={newLocation.street}
                      onChange={(val) =>
                        setNewLocation((s) => ({ ...s, street: val }))
                      }
                      onSelect={(place) => {
                        if (!place) return;
                        const p = parsePlace(place);
                        setNewLocation((s) => ({
                          ...s,
                          street: p.street || "",
                          city: p.city || "",
                          stateProvince: p.stateProvince || "",
                          postalCode: p.postalCode || "",
                          country: p.country || "",
                          geo:
                            p.lat != null && p.lng != null
                              ? { lat: p.lat, lng: p.lng }
                              : null,
                        }));
                      }}
                      placeholder="Start typing address..."
                    />

                    <div className="locations-preview">
                      <div className="locations-preview__title">Preview</div>
                      <div className="locations-preview__line">
                        <strong>
                          {newLocation.name?.trim() || "Location name"}
                        </strong>
                      </div>
                      <div className="locations-preview__line">
                        {[
                          newLocation.street,
                          newLocation.city,
                          newLocation.stateProvince,
                          newLocation.postalCode,
                          newLocation.country,
                        ]
                          .filter(Boolean)
                          .join(", ") || "Address will appear here"}
                      </div>
                    </div>

                    <button
                      className="btn btn-primary locations-add-btn"
                      onClick={async () => {
                        if (!companyId) return;

                        if (
                          !newLocation.name.trim() ||
                          !newLocation.street.trim()
                        ) {
                          toast.error(
                            "Please enter a location name and address.",
                          );
                          return;
                        }

                        const ref = collection(
                          db,
                          "companies",
                          companyId,
                          "locations",
                        );
                        await addDoc(ref, {
                          name: newLocation.name.trim(),
                          address: {
                            street: newLocation.street,
                            city: newLocation.city,
                            stateProvince: newLocation.stateProvince,
                            postalCode: newLocation.postalCode,
                            country: newLocation.country,
                            geo: newLocation.geo || null,
                          },
                          isPrimary: false,
                          active: true,
                          createdAt: serverTimestamp(),
                          createdBy: user.uid,
                        });

                        toast.success("Location added.");

                        // ✅ THIS GOES HERE (exactly here)
                        await updateDoc(doc(db, "companies", companyId), {
                          multiLocationEnabled: true,
                          locationsCount: increment(1),
                        });

                        setNewLocation({
                          name: "",
                          street: "",
                          city: "",
                          stateProvince: "",
                          postalCode: "",
                          country: "",
                          geo: null,
                        });

                        const snap = await getDocs(ref);
                        setLocations(
                          snap.docs.map((d) => ({ id: d.id, ...d.data() })),
                        );
                      }}
                    >
                      Add Location
                    </button>

                    <div className="locations-note">
                      Tip: Primary location is locked because it sets the
                      company default site and timezone baseline.
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ======================= SECURITY & PRIVACY TAB ======================= */}
          {(!isMobile || mobileView === "detail") && activeTab === "privacy" && (
            <section className="card">
              <div className="card-head">
                <div className="card-head__icon">
                  <FiShield />
                </div>
                <div>
                  <h3 className="card-title">Privacy Policy</h3>
                  <p className="card-desc">
                    Read our terms of use and privacy policy
                  </p>
                </div>
              </div>

              <div className="card-body">
                <LegalPolicy />
              </div>
            </section>
          )}

          {/* ======================= BILLING TAB ======================= */}
          {(!isMobile || mobileView === "detail") && activeTab === "billing" && (
            <section className="card">
              <div className="card-head">
                <div className="card-head__icon">
                  <FiCreditCard />
                </div>
                <div>
                  <h3 className="card-title">Plan & Billing</h3>
                  <p className="card-desc">
                    Your current subscription details.
                  </p>
                </div>
              </div>

              <div className="card-body">
                {subLoading ? (
                  <div className="hint">Loading plan…</div>
                ) : isFreePlan ? (
                  <>
                    <div className="plan-summary">
                      <span className="plan-badge">Free Plan</span>

                      <span className="status-pill active">Active</span>
                    </div>

                    <div
                      style={{
                        marginTop: 18,
                        marginBottom: 20,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 30,
                          fontWeight: 700,
                        }}
                      >
                        $0
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                            marginLeft: 4,
                            opacity: 0.7,
                          }}
                        >
                          / month
                        </span>
                      </div>

                      <p className="card-desc" style={{ marginTop: 5 }}>
                        Free forever for teams with up to three employees.
                      </p>
                    </div>

                    <div
                      className="free-plan-usage"
                      style={{
                        padding: 16,
                        border: "1px solid #e2e8f0",
                        borderRadius: 12,
                        marginBottom: 20,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 10,
                        }}
                      >
                        <div>
                          <strong>Employee usage</strong>

                          <div className="hint" style={{ marginTop: 3 }}>
                            Your Free plan supports up to {employeeLimit}{" "}
                            employees.
                          </div>
                        </div>

                        <strong>
                          {employeeUsageLoading
                            ? "Loading…"
                            : `${employeeCount} / ${employeeLimit}`}
                        </strong>
                      </div>

                      <div
                        style={{
                          height: 8,
                          background: "#e8eef3",
                          borderRadius: 999,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${employeeUsagePercent}%`,
                            height: "100%",
                            background: "#294f69",
                            borderRadius: 999,
                            transition: "width 0.25s ease",
                          }}
                        />
                      </div>
                    </div>

                    {!!included.length && (
                      <>
                        <div className="label" style={{ marginTop: 8 }}>
                          Included features
                        </div>

                        <div className="pill-list">
                          {included.map((feature) => (
                            <span key={feature} className="pill">
                              {feature}
                            </span>
                          ))}
                        </div>
                      </>
                    )}

                    <div
                      style={{
                        marginTop: 24,
                        padding: 18,
                        background: "#f3f8fc",
                        borderRadius: 12,
                      }}
                    >
                      <strong>Need more room for your team?</strong>

                      <p
                        className="card-desc"
                        style={{
                          marginTop: 5,
                          marginBottom: 14,
                        }}
                      >
                        Upgrade anytime to add more employees and unlock
                        additional features.
                      </p>

                      <button
                        type="button"
                        className="btn manage-sub-btn"
                        onClick={() =>
                          navigate("/subscription", {
                            state: {
                              intent: "upgrade",
                              from: "settings-billing-free-plan",
                            },
                          })
                        }
                      >
                        Upgrade Plan
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="plan-summary">
                      <span className="plan-badge">{displayPlan}</span>
                      <span
                        className={`status-pill ${displayedStatusLabel
                          .toLowerCase()
                          .replace(/\s/g, "_")}`}
                      >
                        {displayedStatusLabel}
                      </span>
                      {pilotActive && (
                        <span className="status-pill pilot">
                          Pilot Program
                          {pilotEndsOn ? ` • Ends ${pilotEndsOn}` : ""}
                        </span>
                      )}
                    </div>

                    <ul className="plan-meta">
                      {createdOn && (
                        <li>
                          <strong>Started:</strong> {createdOn}
                        </li>
                      )}
                      {isTrialing && trialEndsOn && (
                        <li>
                          <strong>Trial ends:</strong> {trialEndsOn}
                        </li>
                      )}
                      {!isTrialing && currentPeriodEnd && (
                        <li>
                          <strong>Current period ends:</strong>{" "}
                          {currentPeriodEnd}
                        </li>
                      )}
                      {subscription?.planName && (
                        <li>
                          <strong>Plan ID:</strong> {subscription.planName}
                        </li>
                      )}
                      {subscription?.status && (
                        <li>
                          <strong>Status:</strong> {subscription.status}
                        </li>
                      )}
                      {cancelScheduled && cancelsOn && (
                        <li>
                          <strong>Cancels on:</strong> {cancelsOn}
                        </li>
                      )}
                    </ul>

                    {!!included.length && (
                      <>
                        <div className="label" style={{ marginTop: 8 }}>
                          Included features
                        </div>
                        <div className="pill-list">
                          {included.map((f) => (
                            <span key={f} className="pill">
                              {f}
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
              {!isFreePlan && (
                <div className="card-foot">
                  <button
                    className="btn manage-sub-btn"
                    onClick={() =>
                      navigate("/subscription", {
                        state: { intent: "upgrade", from: "settings-billing" },
                      })
                    }
                  >
                    Manage Subscription
                  </button>
                  <button
                    className={`btn btn-light-update ${
                      portalLoading ? "is-loading" : ""
                    }`}
                    disabled={portalLoading}
                    onClick={async () => {
                      try {
                        if (!user?.uid) return;

                        setPortalLoading(true);

                        const url = await getPortalUrl(user.uid);

                        // NOTE: leaving this line will navigate away, so the spinner is mainly for the delay *before* URL returns
                        window.location.href = url;
                      } catch (e) {
                        toast.error(
                          e.message || "Could not open billing portal.",
                        );
                        setPortalLoading(false);
                      }
                    }}
                  >
                    {portalLoading ? (
                      <>
                        <span className="btn-spinner" aria-hidden="true" />
                        Opening…
                      </>
                    ) : (
                      "Update payment method"
                    )}
                  </button>
                </div>
              )}
              {!isFreePlan && (
                <div className="settings-danger-zone">
                  <h3>Cancel Subscription</h3>
                  <p className="settings-danger-text">
                    {pilotActive ? (
                      <>
                        You are currently in the Oiiko Pilot Program
                        {pilotEndsOn ? ` until ${pilotEndsOn}` : ""}.
                        Subscription cancellation is disabled during the pilot.
                        To continue after the pilot ends, choose a plan from
                        “Manage Subscription”.
                      </>
                    ) : cancelScheduled ? (
                      cancelsOn ? (
                        `Your subscription cancellation is scheduled. You’ll keep access until ${cancelsOn}.`
                      ) : (
                        "Your subscription cancellation is scheduled at the end of the current period."
                      )
                    ) : isTrial ? (
                      "This will cancel your subscription after your trial ends. You will not be charged."
                    ) : (
                      "This will cancel your subscription at the end of the current billing period. You’ll keep access until then."
                    )}
                  </p>
                  <button
                    type="button"
                    className="settings-danger-btn"
                    onClick={() => {
                      if (cancelButtonDisabled) return;
                      setFeedbackContext("cancel");
                      setShowFeedbackModal(true);
                    }}
                    disabled={cancelButtonDisabled}
                    title={
                      pilotActive
                        ? "Pilot companies do not have an active subscription to cancel."
                        : ""
                    }
                  >
                    {pilotActive
                      ? "Pilot program (cancellation disabled)"
                      : cancelScheduled
                      ? "Cancellation scheduled"
                      : cancelLoading
                      ? "Cancelling..."
                      : "Cancel subscription"}
                  </button>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {showCancelConfirm && !isFreePlan && (
        <div className="cancel-modal-backdrop">
          <div className="cancel-modal">
            <h3>Cancel subscription?</h3>
            <p>
              {isTrial
                ? "Your subscription will stay active until the end of your trial period. You’ll keep access until then."
                : "Your subscription will stay active until the end of the current billing period. You’ll keep access until then."}
            </p>

            <div className="cancel-modal-actions">
              <button
                type="button"
                className="cancel-modal-secondary"
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelLoading}
              >
                Keep my subscription
              </button>
              <button
                type="button"
                className="cancel-modal-primary"
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
              >
                {cancelLoading ? "Cancelling..." : "Yes, cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFeedbackModal && (
        <div className="cancel-modal-backdrop">
          <div className="cancel-modal">
            <h3>
              {feedbackContext === "delete"
                ? "Before you delete your account…"
                : "Before you cancel your subscription…"}
            </h3>

            <p style={{ marginTop: 8 }}>
              We’d really appreciate it if you could tell us why you’re leaving.
              This helps us improve Oiiko for you and other teams in the future.
            </p>

            <div
              className="feedback-reasons-list"
              style={{
                marginTop: 16,
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {FEEDBACK_REASONS.map((reason) => (
                <label
                  key={reason.id}
                  className="checkbox-tile"
                  style={{ display: "flex", gap: 8, marginBottom: 6 }}
                >
                  <input
                    type="checkbox"
                    className="tiny-checkbox"
                    checked={feedbackReasons.includes(reason.id)}
                    onChange={() => toggleFeedbackReason(reason.id)}
                  />
                  <span>{reason.label}</span>
                </label>
              ))}
            </div>

            <label className="label" style={{ marginTop: 16 }}>
              What could we improve to make Oiiko more useful for you or teams
              like yours?
            </label>
            <textarea
              className="input"
              style={{ minHeight: 90, resize: "vertical" }}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Optional, but very helpful..."
            />

            <div className="cancel-modal-actions" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="cancel-modal-secondary"
                onClick={resetFeedbackState}
                disabled={feedbackSaving}
              >
                Go back
              </button>
              <button
                type="button"
                className="cancel-modal-primary"
                onClick={() => handleFeedbackContinue(true)}
                disabled={feedbackSaving}
              >
                {feedbackSaving
                  ? "Saving..."
                  : feedbackReasons.length || feedbackText.trim()
                  ? "Send feedback & continue"
                  : "Continue without feedback"}
              </button>
            </div>
          </div>
        </div>
      )}
      {editOpen && (
        <div className="cancel-modal-backdrop">
          <div className="cancel-modal">
            <h3>Edit location</h3>

            <p style={{ marginTop: 8 }}>
              Update the location name and address.
            </p>

            <div style={{ marginTop: 14 }} className="form-vertical">
              <label className="label">Location name</label>
              <input
                className="input"
                value={editLocation.name}
                onChange={(e) =>
                  setEditLocation((s) => ({ ...s, name: e.target.value }))
                }
                placeholder="e.g., Main Office, Warehouse"
              />

              <label className="label" style={{ marginTop: 12 }}>
                Address
              </label>
              <AddressAutocomplete
                value={editLocation.street}
                onChange={(val) =>
                  setEditLocation((s) => ({ ...s, street: val }))
                }
                onSelect={(place) => {
                  if (!place) return;
                  const p = parsePlace(place);
                  setEditLocation((s) => ({
                    ...s,
                    street: p.street || "",
                    city: p.city || "",
                    stateProvince: p.stateProvince || "",
                    postalCode: p.postalCode || "",
                    country: p.country || "",
                    geo:
                      p.lat != null && p.lng != null
                        ? { lat: p.lat, lng: p.lng }
                        : null,
                  }));
                }}
                placeholder="Start typing address..."
              />

              <div className="locations-preview" style={{ marginTop: 14 }}>
                <div className="locations-preview__title">Preview</div>
                <div className="locations-preview__line">
                  <strong>
                    {editLocation.name?.trim() || "Location name"}
                  </strong>
                </div>
                <div className="locations-preview__line">
                  {[
                    editLocation.street,
                    editLocation.city,
                    editLocation.stateProvince,
                    editLocation.postalCode,
                    editLocation.country,
                  ]
                    .filter(Boolean)
                    .join(", ") || "Address will appear here"}
                </div>
              </div>
            </div>

            <div className="cancel-modal-actions" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="general-cancelbuttons"
                onClick={closeEditLocation}
                disabled={editSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="general-savebuttons"
                onClick={saveEditedLocation}
                disabled={editSaving}
              >
                {editSaving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmOpen && (
        <div className="cancel-modal-backdrop">
          <div className="cancel-modal">
            <h3>{confirmTitle}</h3>

            {confirmMessage && (
              <p style={{ marginTop: 10, lineHeight: 1.5 }}>{confirmMessage}</p>
            )}

            <div className="cancel-modal-actions" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="cancel-modal-secondary"
                onClick={closeConfirmModal}
              >
                Cancel
              </button>

              <button
                type="button"
                className={
                  confirmDanger ? "settings-danger-btn" : "cancel-modal-primary"
                }
                onClick={handleConfirmPrimary}
              >
                {confirmPrimaryLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
